import React, { useEffect, useMemo, useState } from 'react';
import { STAFF_PATHS } from '../../../../_configuration';
import { generatePath, useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';

import { formatDate } from '../../../lib/helpers/date.helpers';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { checkVideoFilesExist } from '../../../lib/utils/video.utils';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from './my-assignments.messages';
import { clipFilenames } from './assignment-scoring.utils';
import { sortAssignmentsForList, summarizeAssignment } from './my-assignments.utils';

interface IMyAssignmentsProps {
  assignments: ICalibrationAssignment[];
  loading: boolean;
  fetchError: boolean;
  total?: number;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  loadMoreError?: boolean;
}

export const MyAssignments: React.FC<IMyAssignmentsProps> = ({
  assignments, loading, fetchError, total, loadingMore = false, onLoadMore, loadMoreError = false,
}) => {
  const history = useHistory();

  // Warm the shared availability cache from the list too (not only the scorer), then bump a tick to
  // re-summarize so a missing clip is excluded from this list's counts as well. See CLAUDE.md.
  const [availabilityProbed, setAvailabilityProbed] = useState(0);
  const filenamesKey = useMemo(() => assignments.flatMap((a) => clipFilenames(a)).join('|'), [assignments]);
  useEffect(() => {
    const filenames = assignments.flatMap((a) => clipFilenames(a));
    if (!filenames.length) return undefined;
    let cancelled = false;
    checkVideoFilesExist(filenames).then(() => { if (!cancelled) setAvailabilityProbed((n) => n + 1); });
    return () => { cancelled = true; };
    // filenamesKey proxies the filenames read off `assignments`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filenamesKey]);

  // `assignments` is a memoized slice (stable on cache hits); availabilityProbed re-summarizes once
  // the background probe warms the cache, so the counts match what the scorer will show.
  const rows = useMemo(
    () => sortAssignmentsForList(assignments).map((assignment) => ({ assignment, summary: summarizeAssignment(assignment) })),
    [assignments, availabilityProbed],
  );

  // Only blank the list when nothing is cached. cache-and-network hands back the
  // cached rows AND an error on a failed background refresh — keep showing them.
  if (fetchError && !assignments.length) {
    return <Alert severity="error"><IntlMsg {...messages.listLoadError} /></Alert>;
  }

  if (loading && !assignments.length) {
    return <CircularProgress />;
  }

  if (!assignments.length) {
    return <Alert severity="info"><IntlMsg {...messages.listEmpty} /></Alert>;
  }

  const hasMore = typeof total === 'number' && assignments.length < total;

  return (
    <Box>
      {fetchError && (
        <Box mb={1}>
          <Alert severity="warning"><IntlMsg {...messages.listRefreshError} /></Alert>
        </Box>
      )}
      {rows.map(({ assignment, summary }) => {
        const content = (
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box>
                <Typography variant="subtitle1">
                  <IntlMsg
                    {...messages.videosToScore}
                    values={{ count: summary.total, video: pluralize(summary.total, 'video') }}
                  />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <IntlMsg
                    {...messages.assignedDue}
                    values={{
                      assigned: assignment.assignedAt ? formatDate(assignment.assignedAt) : '—',
                      due: assignment.dueAt ? formatDate(assignment.dueAt) : '—',
                    }}
                  />
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Chip
                  size="small"
                  label={<IntlMsg {...messages.scoredCount} values={{ scored: summary.scored, total: summary.total }} />}
                />
                <Box ml={1}>
                  {summary.isCompleted
                    ? <Chip size="small" color="primary" label={<IntlMsg {...messages.statusCompleted} />} />
                    : <Chip size="small" variant="outlined" label={<IntlMsg {...messages.statusPending} />} />}
                </Box>
              </Box>
            </Box>
          </CardContent>
        );

        return (
          <Card key={assignment._id} variant="outlined" style={{ marginBottom: 12 }}>
            {summary.isCompleted
              ? content
              : <CardActionArea onClick={() => history.push(generatePath(STAFF_PATHS.ASSIGNMENT, { assignmentId: assignment._id }))}>{content}</CardActionArea>}
          </Card>
        );
      })}
      {loadMoreError && (
        <Box mt={1}>
          <Alert severity="warning"><IntlMsg {...messages.loadMoreError} /></Alert>
        </Box>
      )}
      {hasMore && (
        <Box display="flex" justifyContent="center" mt={1}>
          <Button onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? <IntlMsg {...messages.loadingMore} /> : <IntlMsg {...messages.loadMore} />}
          </Button>
        </Box>
      )}
    </Box>
  );
};
